import {
  generateJitteredKeyBetween,
  generateNJitteredKeysBetween,
} from 'fractional-indexing-jittered';

/**
 * Fractional-index ordering for ranked lists — today a playbook's categories,
 * the rules within one, and a rule's positions.
 *
 * Ranks are fractional indices: sortable strings where a new value can always
 * be produced between any two existing ones. Moving one row therefore writes
 * one row rather than renumbering everything after it, and a delete leaves no
 * gap to compact, because there is no contiguous sequence to keep contiguous.
 *
 * The jittered variant appends a small random suffix to each generated key.
 * Two people appending at the same time, or dragging into the same gap, would
 * otherwise derive the identical rank from the identical neighbours, and their
 * rows would then sort by whatever tiebreak the query happened to use; the
 * jitter makes that collision vanishingly unlikely without coordination.
 *
 * Rank strings are opaque and meaningful only relative to their siblings —
 * compare them, never parse them, and never compare across lists.
 */

/**
 * A rank that sorts between `before` and `after`. Pass null for either end:
 * `getRankBetween(null, first)` prepends, `getRankBetween(last, null)`
 * appends, and `getRankBetween(null, null)` is the rank of a first row.
 *
 * `before` must sort strictly before `after`; the generator throws otherwise,
 * which is the right outcome for a caller that resolved its neighbours from a
 * stale list.
 */
export function getRankBetween(before: string | null, after: string | null): string {
  return generateJitteredKeyBetween(before, after);
}

/**
 * `count` ranks in ascending order between `before` and `after`, for seeding or
 * backfilling a whole list at once. Cheaper and denser than repeated
 * `getRankBetween` calls, which would each nest inside the previous gap.
 */
export function getRanksBetween(
  before: string | null,
  after: string | null,
  count: number
): string[] {
  if (count <= 0) return [];

  return generateNJitteredKeysBetween(before, after, count);
}

/** Ascending ranks for a brand-new list of `count` rows. */
export function getInitialRanks(count: number): string[] {
  return getRanksBetween(null, null, count);
}

/**
 * The rank an item needs to land at `toIndex` in `ranks`, where `ranks` is the
 * sibling list **excluding the item being moved**. Callers that hold the full
 * list must drop the moved item first, or the neighbours either side of the
 * target gap will be off by one.
 */
export function getRankForIndex(ranks: readonly string[], toIndex: number): string {
  const boundedIndex = Math.max(0, Math.min(toIndex, ranks.length));

  return getRankBetween(ranks[boundedIndex - 1] ?? null, ranks[boundedIndex] ?? null);
}

/**
 * Ascending by rank, then by id so equal ranks still have a stable order.
 *
 * The equal-id case returns 0 rather than falling through to the `<` check,
 * which would report an item as greater than itself. A comparator that does
 * that is inconsistent, and sort implementations are entitled to assume
 * otherwise.
 */
export function compareByRank<TRanked extends { id: string; rank: string }>(
  a: TRanked,
  b: TRanked
): number {
  if (a.rank !== b.rank) return a.rank < b.rank ? -1 : 1;
  if (a.id === b.id) return 0;

  return a.id < b.id ? -1 : 1;
}
